import './index.scss';

import { Component, ReactElement, ReactNode } from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { BraxAppBar, BraxAppBarLinkComponent, BraxIcon, BraxModals, BraxModalsType } from 'design-system';

import t from '../../utils/i18n';
import { withContexts } from '../../utils/with-context';
import client from '../../utils/client';
import User from '../../view-model/users/User';
import UserContext from '../../contexts/User';
import routers from '../../routers';
import Plans from '../../pages/plans/Plans';
import AppBarNotifications from './AppBarNotifications';

export type AppBarProps = RouteComponentProps & {
    modals: BraxModalsType,
    user: User,
    children: ReactNode
};

type AppBarState = {
    open: boolean,
    notifications: boolean,
    active: string,
    inClosedRoute: boolean,
    inUnauthenticatedRoute: boolean
};

const AppBarLink: BraxAppBarLinkComponent = props => 'to' in props ? <Link {...props} /> : <span {...props} />;

export default withRouter(withContexts({
    modals: BraxModals,
    user: UserContext
}, class AppBar extends Component<AppBarProps, AppBarState> {

    state = {
        open: client.isMobile,
        notifications: false,
        active: this.getActivePage(),
        inClosedRoute: this.inClosedRoute(),
        inUnauthenticatedRoute: this.inUnauthenticatedRoute()
    };

    componentDidMount() {
        if (client.isMobile)
            return;

        this.checkIsOpen();

        window.addEventListener('resize', () => this.checkIsOpen());
    }

    componentWillUnmount() {
        window.removeEventListener('resize', () => this.checkIsOpen());
    }

    componentDidUpdate(prevProps: AppBarProps) {
        if (prevProps.location.pathname !== this.props.location.pathname)
            this.setState({
                active: this.getActivePage(),
                inClosedRoute: this.inClosedRoute(),
                inUnauthenticatedRoute: this.inUnauthenticatedRoute()
            });
    }

    getActivePage() {
        const key = this.props.location.pathname.split('/')[1];

        const mapper: Record<string, string> = {
            courses: 'help'
        };

        return mapper[key] || key;
    }

    checkIsOpen() {
        const open = window.innerWidth >= 992;

        this.setState(prevState => prevState.open !== open ? { open } : null);
    }

    inUnauthenticatedRoute() {
        return routers.unauthenticated.findIndex(
            route => this.props.location.pathname === route.path || this.props.location.pathname === `${route.path}/`
        ) !== -1;
    }

    inClosedRoute() {
        return this.props.location.pathname.match(/\/editor\/([0-9]+|create)/) !== null;
    }

    showNotifications = () => this.setState({ notifications: true });

    closeNotifications = () => this.setState({ notifications: false });

    AppBarNotificationsWrapper = (props: { children: ReactElement }) => <AppBarNotifications {...props} onShow={this.showNotifications} onClose={this.closeNotifications} />;

    render() {
        const { avatar, firstName, lastName } = this.props.user ?? {};

        const icon = avatar ? {
            src: avatar,
            alt: t('Imagem do seu usuário')
        } : {
            firstName,
            lastName
        };

        return (
            <div className={`app-bar ${this.state.inUnauthenticatedRoute ? 'hidden' : ''}`}>
                <BraxAppBar
                    linkComponent={AppBarLink}
                    menuItems={[
                        [{
                            type: 'logo',
                            key: 'projects',
                            variant: 'white'
                        }],
                        [{
                            type: 'icon',
                            key: 'projects',
                            icon: 'grid',
                            label: t('Projetos')
                        }, {
                            type: 'icon',
                            key: 'recordings',
                            icon: 'video',
                            label: t('Vídeos')
                        }, {
                            type: 'icon',
                            key: 'street-view',
                            icon: 'map',
                            label: t('GSV')
                        }, {
                            type: 'icon',
                            key: 'configurations',
                            icon: 'gear',
                            label: t('Configs')
                        }],
                        [{
                            type: 'icon',
                            key: 'plans',
                            icon: 'money',
                            label: t('Planos'),
                            onClick: () => this.props.modals.custom(<Plans />)
                        }, {
                            type: 'icon',
                            key: 'help',
                            icon: 'question-mark',
                            label: t('Ajuda')
                        }, {
                            type: 'icon',
                            key: 'notifications',
                            icon: <BraxIcon icon="bell" className="notification-icon" />,
                            label: t('Notifi.'),
                            wrapper: this.AppBarNotificationsWrapper
                        }, {
                            type: 'avatar',
                            key: 'user',
                            ...icon
                        }]
                    ]}
                    active={this.state.active}
                    open={this.state.open || this.state.notifications}
                >
                    { this.props.children }
                </BraxAppBar>
            </div>
        );
    }

}));
